import { type Message } from '@ag-ui/core';

import { BRAND_KIT_SETUP_TOOL_NAME, type BrandKitSetupResult, BrandKitSetupResultSchema } from './brandKitSetup';
import { DETECT_TEXT_TOOL_NAME, type DetectTextResult, DetectTextResultSchema } from './detectText';
import {
  ELICIT_MISSING_CONTEXT_TOOL_NAME,
  type ElicitMissingContextResult,
  ElicitMissingContextResultSchema,
} from './elicitMissingContext';
import {
  FILE_EDIT_COMPLETE_TOOL_NAME,
  type FileEditCompleteResult,
  FileEditCompleteResultSchema,
} from './fileEditComplete';
import { GENERATE_TEXT_TOOL_NAME, type GenerateTextResult, GenerateTextResultSchema } from './generateText';
import {
  SUBMIT_TRANSLATIONS_TOOL_NAME,
  type SubmitTranslationsResult,
  SubmitTranslationsResultSchema,
} from './submitTranslations';
export type ToolCallResultParams = {
  toolCallName: string;
  toolCallResult: unknown;
};

export type ValidatedToolCallResultParams =
  | {
      toolCallName: typeof BRAND_KIT_SETUP_TOOL_NAME;
      toolCallResult: BrandKitSetupResult;
    }
  | {
      toolCallName: typeof ELICIT_MISSING_CONTEXT_TOOL_NAME;
      toolCallResult: ElicitMissingContextResult;
    }
  | {
      toolCallName: typeof FILE_EDIT_COMPLETE_TOOL_NAME;
      toolCallResult: FileEditCompleteResult;
    }
  | {
      toolCallName: typeof GENERATE_TEXT_TOOL_NAME;
      toolCallResult: GenerateTextResult;
    }
  | {
      toolCallName: typeof DETECT_TEXT_TOOL_NAME;
      toolCallResult: DetectTextResult;
    }
  | {
      toolCallName: typeof SUBMIT_TRANSLATIONS_TOOL_NAME;
      toolCallResult: SubmitTranslationsResult;
    };

export type ToolResultValidationResult =
  | { error: Error; success: false; toolCallId: string }
  | { params: ToolCallResultParams; success: true };

export function findToolNameByCallId(toolCallId: string, messages: Message[]): string | null {
  for (const message of messages) {
    if (message.role === 'assistant' && message.toolCalls) {
      const toolCall = message.toolCalls.find((tc) => tc.id === toolCallId);

      if (toolCall) {
        return toolCall.function.name;
      }
    }
  }
  return null;
}

/** Parse tool result content and validate against schema for registered tools. */
export function parseToolContent(toolName: string | null, content: string): unknown {
  if (!content) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    return null;
  }

  if (Array.isArray(parsed)) {
    parsed = parsed[0];
  }

  switch (toolName) {
    case BRAND_KIT_SETUP_TOOL_NAME: {
      const result = BrandKitSetupResultSchema.safeParse(parsed);

      return result.success ? result.data : null;
    }
    case ELICIT_MISSING_CONTEXT_TOOL_NAME: {
      const result = ElicitMissingContextResultSchema.safeParse(parsed);

      return result.success ? result.data : null;
    }
    case FILE_EDIT_COMPLETE_TOOL_NAME: {
      const result = FileEditCompleteResultSchema.safeParse(parsed);

      return result.success ? result.data : null;
    }
    case GENERATE_TEXT_TOOL_NAME: {
      const result = GenerateTextResultSchema.safeParse(parsed);

      return result.success ? result.data : null;
    }
    case DETECT_TEXT_TOOL_NAME: {
      const result = DetectTextResultSchema.safeParse(parsed);

      return result.success ? result.data : null;
    }
    case SUBMIT_TRANSLATIONS_TOOL_NAME: {
      const result = SubmitTranslationsResultSchema.safeParse(parsed);

      return result.success ? result.data : null;
    }
    default:
      return parsed;
  }
}

/** Type guard that narrows a ToolCallResultParams to elicit_missing_context. */
export const isElicitMissingContextResult = <T extends ToolCallResultParams>(
  params: T,
): params is T & {
  toolCallName: typeof ELICIT_MISSING_CONTEXT_TOOL_NAME;
  toolCallResult: ElicitMissingContextResult;
} => {
  return params.toolCallName === ELICIT_MISSING_CONTEXT_TOOL_NAME && params.toolCallResult != null;
};

/** Type guard that narrows a ToolCallResultParams (or any super-type of it) to file_edit_complete. */
export const isFileEditCompleteResult = <T extends ToolCallResultParams>(
  params: T,
): params is T & { toolCallName: typeof FILE_EDIT_COMPLETE_TOOL_NAME; toolCallResult: FileEditCompleteResult } => {
  return params.toolCallName === FILE_EDIT_COMPLETE_TOOL_NAME && params.toolCallResult != null;
};

/** Type guard that narrows a ToolCallResultParams (or any super-type of it) to generate_text. */
export const isGenerateTextResult = <T extends ToolCallResultParams>(
  params: T,
): params is T & { toolCallName: typeof GENERATE_TEXT_TOOL_NAME; toolCallResult: GenerateTextResult } => {
  return params.toolCallName === GENERATE_TEXT_TOOL_NAME && params.toolCallResult != null;
};

/** Type guard that narrows a ToolCallResultParams to detect_text. */
export const isDetectTextResult = <T extends ToolCallResultParams>(
  params: T,
): params is T & {
  toolCallName: typeof DETECT_TEXT_TOOL_NAME;
  toolCallResult: DetectTextResult;
} => {
  return params.toolCallName === DETECT_TEXT_TOOL_NAME && params.toolCallResult != null;
};

/** Type guard that narrows a ToolCallResultParams to submit_translations. */
export const isSubmitTranslationsResult = <T extends ToolCallResultParams>(
  params: T,
): params is T & {
  toolCallName: typeof SUBMIT_TRANSLATIONS_TOOL_NAME;
  toolCallResult: SubmitTranslationsResult;
} => {
  return params.toolCallName === SUBMIT_TRANSLATIONS_TOOL_NAME && params.toolCallResult != null;
};

export function validateToolCallResult(
  toolCallId: string,
  content: string,
  messages: Message[],
): ToolResultValidationResult {
  const toolCallName = findToolNameByCallId(toolCallId, messages);

  if (!toolCallName) {
    return {
      error: new Error(`Could not find tool call with id: ${toolCallId}`),
      success: false,
      toolCallId,
    };
  }

  const toolCallResult = parseToolContent(toolCallName, content);

  if (toolCallResult === null) {
    return {
      error: new Error(`Failed to parse or validate tool result for: ${toolCallName}`),
      success: false,
      toolCallId,
    };
  }

  return {
    params: { toolCallName, toolCallResult },
    success: true,
  };
}
